/**
 * What a grown-up gets called for keeping their word.
 *
 * Every rule here follows from one decision: this is positive-only and never
 * leaves the family.
 *
 * - **Positive-only**, because anything that costs a parent will be paid for by
 *   the child. A grown-up who is scolded by an app for a late zoo trip stops
 *   approving zoo trips, and the child loses the whole mechanism rather than
 *   one reward.
 * - **Never comparative**, because the thing a leaderboard would actually rank
 *   is disposable income, in front of children.
 * - **Slow to award and never taken away.** A title that can drop is a title
 *   worth pressing a dependent eight-year-old about.
 *
 * The incentive that does the real work is elsewhere anyway: the child's
 * stardust is sitting in a promise that has not arrived yet, and they can take
 * it back. This is the warm part, not the lever.
 */
export const PARENT_TITLES = [
    { kept: 12, title: "Star keeper" },
    { kept: 6, title: "Keeps their word" },
    { kept: 3, title: "Good for it" },
    { kept: 1, title: "Made it happen" },
];
export function parentTitle(kept) {
    return PARENT_TITLES.find((tier) => kept >= tier.kept)?.title ?? null;
}
/** Median rather than mean: one forgotten promise should not rewrite the story. */
export function medianDays(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    const value = sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
    return Math.round(value * 10) / 10;
}
export function buildParentRecord(input) {
    return {
        kept: input.kept,
        missed: input.missed,
        typicalDays: medianDays(input.daysToDeliver),
        title: parentTitle(input.kept),
    };
}
/**
 * How a child is told about a promise still in the air.
 *
 * Never accusing, and never a date — the same rule the pace message follows.
 * "Soon" is a real answer from a grown-up and the app should not contradict it.
 */
export function waitingCopy(days) {
    if (days <= 0)
        return "Your grown-up said yes. Tell me when it happens.";
    if (days === 1)
        return "Said yes yesterday. Tell me when it happens.";
    if (days < 7)
        return `Said yes ${days} days ago. Tell me when it happens.`;
    if (days < 14)
        return "Said yes over a week ago. Still waiting?";
    return "This one has been waiting a while. You can take your stardust back.";
}
/** The grown-up's side of the same wait. Plain, factual, no guilt-tripping. */
export function promiseAgeCopy(days) {
    if (days <= 0)
        return "You said yes today.";
    if (days === 1)
        return "You said yes yesterday.";
    return `You said yes ${days} days ago.`;
}
/** Whole days between two moments, floored — a promise is not late by hours. */
export function daysBetween(from, to) {
    return Math.max(0, Math.floor((to.getTime() - from.getTime()) / 86_400_000));
}
//# sourceMappingURL=promises.js.map